import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from '../ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../ui/select';
import {
  Package, Loader2, AlertTriangle, Zap, X
} from 'lucide-react';
import { toast } from 'sonner';
import { SearchableSelect, MEXICAN_STATES, COUNTRIES } from '../SearchableSelect';
import ClientSuggestion from '../ClientSuggestion';

const EmergencyModeModal = ({
  isOpen, setIsOpen,
  emergencyForm, setEmergencyForm,
  emergencyAvailableEquipment, setEmergencyAvailableEquipment,
  emergencyCategoriesWithAvailability, setEmergencyCategoriesWithAvailability,
  emergencyLoading, setEmergencyLoading,
  responsibles, clients, locationsWithAvailability, locations, categories, equipment,
  api, loadData,
  searchBySerial = true, setSearchBySerial,
  searchByReceptor = false, setSearchByReceptor
}) => {

  const handleSubmit = async () => {
    setEmergencyLoading(true);
    try {
      const payload = {
        type: 'salida',
        movement_subtype: 'evento',
        responsible_id: emergencyForm.responsible_id,
        event_date: emergencyForm.event_date,
        event_time: emergencyForm.event_time || new Date().toTimeString().slice(0, 5),
        event_name: emergencyForm.event_name ? `[EMERGENCIA] ${emergencyForm.event_name}` : '[EMERGENCIA]',
        control_number: emergencyForm.control_number || '',
        client_name: emergencyForm.client_name,
        client_contact: emergencyForm.client_contact || '',
        client_id: emergencyForm.client_id || null,
        event_city: emergencyForm.event_city || 'CDMX',
        event_state: emergencyForm.event_state || 'Ciudad de Mexico',
        event_country: emergencyForm.event_country || 'Mexico',
        notes: emergencyForm.notes || 'Salida de emergencia - Sin verificación de contraseña',
        items: emergencyForm.items.map(item => ({
          equipment_id: item.equipment_id,
          quantity: item.quantity,
          location_id: item.location_id,
          serial_numbers: (item.selected_units || []).join(',') || item.selected_serial || '',
          condition: 'bueno'
        })),
        equipment_state_checked: false,
        equipment_no_review: true,
        send_email_notification: true
      };
      
      const response = await api().post('/movements', payload);
      const movementId = response.data?.id;
      
      toast.success('Salida de emergencia registrada');
      
      // Auto-download PDF
      if (movementId) {
        try {
          const pdfResponse = await api().get(`/movements/${movementId}/pdf`, {
            responseType: 'blob'
          });
          const url = window.URL.createObjectURL(new Blob([pdfResponse.data]));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `Emergencia_${movementId.slice(0, 8)}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          toast.success('PDF de responsiva descargado');
        } catch (pdfError) {
          toast.error('No se pudo descargar el PDF');
        }
      }
      
      setIsOpen(false);
      setEmergencyForm({ 
        responsible_id: '', 
        event_name: '', 
        event_date: new Date().toISOString().split('T')[0],
        event_time: new Date().toTimeString().slice(0, 5),
        client_name: '',
        client_contact: '',
        control_number: '', 
        location_id: '', 
        category_id: '', 
        items: [], 
        notes: '' 
      });
      setEmergencyAvailableEquipment([]);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar');
    } finally {
      setEmergencyLoading(false);
    }
  };

  const handleLocationChange = async (value) => {
    setEmergencyForm({...emergencyForm, location_id: value, category_id: ''});
    try {
      const [equipmentRes, categoriesRes] = await Promise.all([
        api().get(`/locations/${value}/available-equipment`),
        api().get(`/categories/with-availability?location_id=${value}`)
      ]);
      setEmergencyAvailableEquipment(equipmentRes.data || []);
      setEmergencyCategoriesWithAvailability(categoriesRes.data || []);
    } catch {
      setEmergencyAvailableEquipment([]);
      setEmergencyCategoriesWithAvailability([]);
    }
  };

  const handleAddEquipment = (value) => {
    const eq = emergencyAvailableEquipment.find(e => e.equipment_id === value);
    if (eq && !emergencyForm.items.find(i => i.equipment_id === value)) {
      const cat = categories.find(c => c.id === eq.category_id);
      setEmergencyForm({
        ...emergencyForm,
        items: [...emergencyForm.items, { 
          equipment_id: value, 
          name: eq.equipment_name, 
          category: cat?.name || 'Sin categoría',
          quantity: 1,
          max_available: eq.available_count,
          location_id: emergencyForm.location_id,
          location_name: locations.find(l => l.id === emergencyForm.location_id)?.name || '',
          available_serials: eq.units?.map(u => u.serial_number) || [],
          available_units_data: eq.units?.map(u => ({ serial_number: u.serial_number, receptor_number: u.receptor_number || '' })) || [],
          selected_serial: ''
        }]
      });
    }
  };

  const handleRemoveItem = (idx) => {
    setEmergencyForm({
      ...emergencyForm,
      items: emergencyForm.items.filter((_, i) => i !== idx)
    });
  };

  const updateItemField = (idx, field, value) => {
    const newItems = [...emergencyForm.items];
    newItems[idx][field] = value;
    setEmergencyForm({...emergencyForm, items: newItems});
  };

  const renderClientSubSelectors = () => {
    if (!emergencyForm.client_id) return null;
    const client = clients.find(c => c.id === emergencyForm.client_id);
    if (!client) return null;
    const allContacts = [
      ...(client.contact_name ? [{ name: client.contact_name, email: client.email, phone: client.phone, city: client.city, state: client.state, country: client.country }] : []),
      ...(client.contacts || [])
    ];
    const uniqueContacts = [...new Set(allContacts.map(c => c.name).filter(Boolean))];
    const uniqueCities = [...new Set([client.city, ...allContacts.map(c => c.city)].filter(Boolean))];
    const uniqueStates = [...new Set([client.state, ...allContacts.map(c => c.state)].filter(Boolean))];
    const uniqueCountries = [...new Set([client.country, ...allContacts.map(c => c.country)].filter(Boolean))];
    const hasMultiple = uniqueContacts.length > 1 || uniqueCities.length > 1 || uniqueStates.length > 1 || uniqueCountries.length > 1;
    if (!hasMultiple) return null;
    return (
      <div className="p-3 border rounded-lg bg-blue-500/5 border-blue-500/20 space-y-2">
        <p className="text-xs font-medium text-blue-500">Datos del cliente (seleccionar):</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {uniqueContacts.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Contacto</Label>
              <Select value={emergencyForm.client_contact || ''} onValueChange={v => setEmergencyForm({...emergencyForm, client_contact: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Contacto" /></SelectTrigger>
                <SelectContent>
                  {uniqueContacts.map(n => <SelectItem key={n} value={n}>{n}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {uniqueCities.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Ciudad</Label>
              <Select value={emergencyForm.event_city || ''} onValueChange={v => setEmergencyForm({...emergencyForm, event_city: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Ciudad" /></SelectTrigger>
                <SelectContent>
                  {uniqueCities.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {uniqueStates.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Estado</Label>
              <Select value={emergencyForm.event_state || ''} onValueChange={v => setEmergencyForm({...emergencyForm, event_state: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Estado" /></SelectTrigger>
                <SelectContent>
                  {uniqueStates.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
          {uniqueCountries.length > 1 && (
            <div className="space-y-1">
              <Label className="text-xs">Pais</Label>
              <Select value={emergencyForm.event_country || ''} onValueChange={v => setEmergencyForm({...emergencyForm, event_country: v})}>
                <SelectTrigger className="h-7 text-xs"><SelectValue placeholder="Pais" /></SelectTrigger>
                <SelectContent>
                  {uniqueCountries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-500">
            <Zap className="h-5 w-5" />
            Modo Emergencia (Bomberazo)
          </DialogTitle>
          <DialogDescription>
            Salida rápida. El equipo saldrá <b>sin revisión de contraseña</b> y se notificará a los administradores.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Row 1: Responsible and Client */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Responsable *</Label>
              <Select
                value={emergencyForm.responsible_id}
                onValueChange={(value) => setEmergencyForm({...emergencyForm, responsible_id: value})}
              >
                <SelectTrigger data-testid="emergency-responsible">
                  <SelectValue placeholder="Seleccionar responsable" />
                </SelectTrigger>
                <SelectContent>
                  {responsibles.map((r) => (
                    <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 relative">
              <Label>Cliente *</Label>
              <Input
                value={emergencyForm.client_name}
                onChange={(e) => setEmergencyForm({...emergencyForm, client_name: e.target.value})}
                placeholder="Nombre del cliente"
                data-testid="emergency-client"
                autoComplete="off"
              />
              {!emergencyForm.client_id && (
                <ClientSuggestion
                  inputValue={emergencyForm.client_name}
                  clients={clients}
                  onSelect={(client) => {
                    setEmergencyForm({
                      ...emergencyForm,
                      client_id: client.id,
                      client_name: client.name,
                      client_contact: client.contact_name || emergencyForm.client_contact,
                      event_city: client.city || emergencyForm.event_city,
                      event_state: client.state || emergencyForm.event_state,
                      event_country: client.country || emergencyForm.event_country,
                    });
                  }}
                />
              )}
            </div>
          </div>

          {/* Client Quick Select for Emergency */}
          {clients.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Seleccionar cliente existente (opcional)</Label>
              <Select
                value={emergencyForm.client_id || ''}
                onValueChange={(clientId) => {
                  if (clientId === 'none') {
                    setEmergencyForm({ ...emergencyForm, client_id: '' });
                    return;
                  }
                  const client = clients.find(c => c.id === clientId);
                  if (client) {
                    setEmergencyForm({
                      ...emergencyForm,
                      client_id: clientId,
                      client_name: client.name,
                      client_contact: client.contact_name || emergencyForm.client_contact,
                      event_city: client.city || emergencyForm.event_city,
                      event_state: client.state || emergencyForm.event_state,
                    });
                  }
                }}
              >
                <SelectTrigger className="h-8 text-xs" data-testid="emergency-client-select">
                  <SelectValue placeholder="Vincular a cliente registrado..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin vincular</SelectItem>
                  {clients.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}{c.company ? ` (${c.company})` : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Client Data Sub-Selectors */}
          {renderClientSubSelectors()}

          {/* Row 2: Client Contact and Event Name */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Contacto del Cliente</Label>
              <Input
                value={emergencyForm.client_contact}
                onChange={(e) => setEmergencyForm({...emergencyForm, client_contact: e.target.value})}
                placeholder="Telefono o email (opcional)"
                data-testid="emergency-client-contact"
              />
            </div>
            <div className="space-y-2">
              <Label>Nombre del Evento/Destino</Label>
              <Input
                value={emergencyForm.event_name}
                onChange={(e) => setEmergencyForm({...emergencyForm, event_name: e.target.value})}
                placeholder="Ej: Evento Corporativo, Boda, etc."
                data-testid="emergency-event-name"
              />
            </div>
          </div>

          {/* City, State, Country */}
          <div className="grid grid-cols-3 gap-3">
            <div className="space-y-2">
              <Label className="text-xs">Ciudad</Label>
              <Input
                value={emergencyForm.event_city}
                onChange={(e) => setEmergencyForm({...emergencyForm, event_city: e.target.value})}
                placeholder="Ciudad"
                className="h-8 text-xs"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Estado</Label>
              <SearchableSelect
                value={emergencyForm.event_state}
                onValueChange={(v) => setEmergencyForm({...emergencyForm, event_state: v})}
                options={MEXICAN_STATES}
                placeholder="Estado"
                showManualOption={true}
                manualLabel="Agregar manualmente"
                size="sm"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">Pais</Label>
              <SearchableSelect
                value={emergencyForm.event_country}
                onValueChange={(v) => setEmergencyForm({...emergencyForm, event_country: v})}
                options={COUNTRIES}
                placeholder="Pais"
                showManualOption={true}
                manualLabel="Agregar manualmente"
                size="sm"
              />
            </div>
          </div>

          {/* Row 3: Control Number, Date and Time */}
          <div className="grid grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label>No. Control Evento *</Label>
              <Input
                value={emergencyForm.control_number}
                onChange={(e) => {
                  const onlyDigits = e.target.value.replace(/\D/g, '').slice(0, 5);
                  setEmergencyForm({...emergencyForm, control_number: onlyDigits});
                }}
                placeholder="12345"
                inputMode="numeric"
                pattern="\d{5}"
                maxLength={5}
                data-testid="emergency-control-number"
                className={emergencyForm.control_number && emergencyForm.control_number.length !== 5 ? 'border-red-500 focus-visible:ring-red-500' : ''}
              />
              <p className="text-[10px] text-muted-foreground">5 dígitos numéricos</p>
            </div>
            <div className="space-y-2">
              <Label>Fecha del Evento *</Label>
              <Input
                type="date"
                value={emergencyForm.event_date}
                onChange={(e) => setEmergencyForm({...emergencyForm, event_date: e.target.value})}
                data-testid="emergency-date"
              />
            </div>
            <div className="space-y-2">
              <Label>Hora del Evento *</Label>
              <Input
                type="time"
                value={emergencyForm.event_time}
                onChange={(e) => setEmergencyForm({...emergencyForm, event_time: e.target.value})}
                data-testid="emergency-time"
              />
            </div>
          </div>

          {/* Equipment Selection */}
          <Card className="bg-red-500/5 border-red-500/20">
            <CardHeader className="py-3">
              <CardTitle className="text-sm text-red-400">Agregar Equipo</CardTitle>
            </CardHeader>
            <CardContent className="pb-4 space-y-3">
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label className="text-xs">1. Ubicación Origen *</Label>
                  <Select
                    value={emergencyForm.location_id}
                    onValueChange={handleLocationChange}
                  >
                    <SelectTrigger data-testid="emergency-location">
                      <SelectValue placeholder="Seleccionar ubicación" />
                    </SelectTrigger>
                    <SelectContent>
                      {locationsWithAvailability.map(loc => (
                        <SelectItem key={loc.id} value={loc.id}>{loc.name} ({loc.available_count} disp.)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">2. Categoría (opcional)</Label>
                  <Select
                    value={emergencyForm.category_id || 'all'}
                    onValueChange={(value) => setEmergencyForm({...emergencyForm, category_id: value === 'all' ? '' : value})}
                    disabled={!emergencyForm.location_id}
                  >
                    <SelectTrigger data-testid="emergency-category">
                      <SelectValue placeholder="Todas las categorías" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las categorías</SelectItem>
                      {emergencyCategoriesWithAvailability.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name} ({cat.available_count} disp.)</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">3. Equipo *</Label>
                  <Select
                    disabled={!emergencyForm.location_id}
                    onValueChange={handleAddEquipment}
                  >
                    <SelectTrigger data-testid="emergency-equipment">
                      <SelectValue placeholder={emergencyForm.location_id ? "Seleccionar equipo" : "Primero selecciona ubicación"} />
                    </SelectTrigger>
                    <SelectContent>
                      {emergencyAvailableEquipment
                        .filter(eq => !emergencyForm.category_id || equipment.find(e => e.id === eq.equipment_id)?.category_id === emergencyForm.category_id)
                        .map((eq) => (
                          <SelectItem key={eq.equipment_id} value={eq.equipment_id}>
                            {eq.equipment_name} (Disp: {eq.available_count})
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </CardContent>
          </Card>
            
          {/* Selected Items with Details */}
          {emergencyForm.items.length > 0 && (
            <div className="space-y-2">
              <Label>Equipos seleccionados ({emergencyForm.items.length})</Label>
              <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                {emergencyForm.items.map((item, idx) => (
                  <div key={idx} className="p-3 space-y-2">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Package className="h-4 w-4 text-muted-foreground" />
                        <span className="font-medium">{item.name}</span>
                        <Badge variant="outline" className="text-xs">{item.category}</Badge>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-destructive"
                        onClick={() => handleRemoveItem(idx)}
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                    
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <Label className="text-xs">Cantidad</Label>
                        <Input
                          type="number"
                          min="1"
                          max={item.max_available || 10}
                          value={item.quantity}
                          onChange={(e) => {
                            const newItems = [...emergencyForm.items];
                            newItems[idx].quantity = parseInt(e.target.value) || 1;
                            setEmergencyForm({...emergencyForm, items: newItems});
                          }}
                          className="h-8"
                        />
                      </div>
                      <div>
                        <Label className="text-xs">Origen</Label>
                        <Input value={item.location_name} disabled className="h-8 bg-muted/50" />
                      </div>
                      {item.available_serials && item.available_serials.length > 0 && (
                        <div className="col-span-3 space-y-2">
                          <div className="flex items-center justify-between">
                            <Label className="text-xs">Seleccionar unidades (opcional)</Label>
                            <div className="flex gap-1">
                              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2"
                                onClick={() => {
                                  const newItems = [...emergencyForm.items];
                                  const filtered = (item.available_units_data || item.available_serials).filter(u => {
                                    const serial = typeof u === 'string' ? u : u.serial_number;
                                    const receptor = typeof u === 'object' ? (u.receptor_number || '') : '';
                                    if (!item.serial_search) return true;
                                    const q = item.serial_search.toLowerCase();
                                    const matchS = searchBySerial && serial.toLowerCase().includes(q);
                                    const matchR = searchByReceptor && receptor.toLowerCase().includes(q);
                                    return matchS || matchR || (!searchBySerial && !searchByReceptor);
                                  });
                                  const filteredSerials = filtered.map(u => typeof u === 'string' ? u : u.serial_number);
                                  newItems[idx].selected_units = filteredSerials;
                                  newItems[idx].quantity = filteredSerials.length;
                                  setEmergencyForm({...emergencyForm, items: newItems});
                                }}>Seleccionar todos</Button>
                              <Button type="button" variant="ghost" size="sm" className="h-6 text-xs px-2"
                                onClick={() => {
                                  const newItems = [...emergencyForm.items];
                                  newItems[idx].selected_units = [];
                                  newItems[idx].quantity = 1;
                                  setEmergencyForm({...emergencyForm, items: newItems});
                                }}>Limpiar</Button>
                            </div>
                          </div>
                          {/* Search mode toggles */}
                          <div className="flex items-center gap-4 px-1">
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={searchBySerial}
                                onCheckedChange={(checked) => setSearchBySerial?.(checked)}
                                data-testid="emergency-search-by-serial"
                              />
                              <span className="text-xs text-muted-foreground">No. de Serie</span>
                            </label>
                            <label className="flex items-center gap-1.5 cursor-pointer">
                              <Checkbox
                                checked={searchByReceptor}
                                onCheckedChange={(checked) => setSearchByReceptor?.(checked)}
                                data-testid="emergency-search-by-receptor"
                              />
                              <span className="text-xs text-muted-foreground">No. de Equipo</span>
                            </label>
                          </div>
                          <Input
                            placeholder={searchByReceptor && !searchBySerial ? "Buscar por número de equipo..." : "Buscar por serie o equipo..."}
                            value={item.serial_search || ''}
                            onChange={(e) => updateItemField(idx, 'serial_search', e.target.value)}
                            className="h-8 text-xs"
                          />
                          <div className="border rounded-md p-2 max-h-32 overflow-y-auto">
                            <div className="grid grid-cols-2 gap-2">
                              {(item.available_units_data || item.available_serials.map(s => ({serial_number: s, receptor_number: ''})))
                                .filter(u => {
                                  const serial = typeof u === 'string' ? u : u.serial_number;
                                  const receptor = typeof u === 'object' ? (u.receptor_number || '') : '';
                                  if (!item.serial_search) return true;
                                  const q = item.serial_search.toLowerCase();
                                  const matchS = searchBySerial && serial.toLowerCase().includes(q);
                                  const matchR = searchByReceptor && receptor.toLowerCase().includes(q);
                                  return matchS || matchR || (!searchBySerial && !searchByReceptor);
                                })
                                .map(u => {
                                  const serial = typeof u === 'string' ? u : u.serial_number;
                                  const receptor = typeof u === 'object' ? (u.receptor_number || '') : '';
                                  return (
                                <label key={serial} className="flex items-center gap-2 p-1 rounded hover:bg-muted/50 cursor-pointer">
                                  <Checkbox
                                    checked={(item.selected_units || []).includes(serial)}
                                    onCheckedChange={(checked) => {
                                      const newItems = [...emergencyForm.items];
                                      const units = newItems[idx].selected_units || [];
                                      if (checked) {
                                        newItems[idx].selected_units = [...units, serial];
                                        newItems[idx].quantity = units.length + 1;
                                      } else {
                                        newItems[idx].selected_units = units.filter(s => s !== serial);
                                        newItems[idx].quantity = Math.max(1, units.length - 1);
                                      }
                                      setEmergencyForm({...emergencyForm, items: newItems});
                                    }}
                                  />
                                  <div className="flex flex-col">
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs font-mono">{serial}</span>
                                      {(serial === 'NA' || serial === 'N/A') && (
                                        <AlertTriangle className="h-3 w-3 text-amber-500" title="Sin número de serie" />
                                      )}
                                    </div>
                                    {receptor && (
                                      <span className="text-[10px] text-muted-foreground">{receptor}</span>
                                    )}
                                  </div>
                                </label>
                                );
                              })}
                            </div>
                          </div>
                          {(item.selected_units || []).length > 0 && (
                            <p className="text-xs text-muted-foreground">{item.selected_units.length} unidad(es) seleccionada(s)</p>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="space-y-2">
            <Label>Notas (opcional)</Label>
            <Textarea
              value={emergencyForm.notes}
              onChange={(e) => setEmergencyForm({...emergencyForm, notes: e.target.value})}
              placeholder="Motivo de la urgencia..."
              rows={2}
            />
          </div>

          {/* Warning */}
          <div className="bg-amber-500/10 border border-amber-500/30 rounded-lg p-3 text-sm">
            <div className="flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 mt-0.5" />
              <div>
                <p className="font-medium text-amber-500">Aviso importante</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Esta salida quedará marcada como "sin revisión" y se notificará a los administradores por email.
                  La fecha/hora se registrará automáticamente.
                </p>
              </div>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setIsOpen(false)}>
            Cancelar
          </Button>
          <Button 
            className="bg-red-500 hover:bg-red-600 text-white"
            disabled={emergencyLoading || !emergencyForm.responsible_id || !emergencyForm.client_name || !emergencyForm.event_date || !(emergencyForm.control_number && emergencyForm.control_number.length === 5) || emergencyForm.items.length === 0}
            onClick={handleSubmit}
            data-testid="emergency-submit-btn"
          >
            {emergencyLoading && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            <Zap className="h-4 w-4 mr-2" />
            Registrar Emergencia
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EmergencyModeModal;
